/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8002";
const ADMIN_ROUTE = import.meta.env.VITE_ADMIN_ROUTE || "console";
const SESSION_KEY = "bb_session_id";
const UTM_KEY = "bb_utm_params";
const GA_SETTINGS_KEY = "bb_ga_settings";
const GA_SETTINGS_TTL = 5 * 60 * 1000; // 5 minutes cache

interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

interface AnalyticsContextValue {
  sessionId: string;
  trackEvent: (eventType: string, metadata?: Record<string, unknown>) => void;
  trackFeature: (
    featureName: string,
    metadata?: Record<string, unknown>,
  ) => void;
  trackSearch: (query: string, resultsCount?: number) => void;
  trackExport: (exportType: string, details?: Record<string, unknown>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

/**
 * Get or create a session ID
 */
function getOrCreateSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
}

/**
 * Capture UTM params from URL
 */
function captureUtmParams(): UtmParams {
  const storedParams = sessionStorage.getItem(UTM_KEY);
  if (storedParams) {
    return JSON.parse(storedParams);
  }

  const params = new URLSearchParams(window.location.search);
  const utmParams: UtmParams = {};

  [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
  ].forEach((key) => {
    const value = params.get(key);
    if (value) {
      utmParams[key as keyof UtmParams] = value;
    }
  });

  if (Object.keys(utmParams).length > 0) {
    sessionStorage.setItem(UTM_KEY, JSON.stringify(utmParams));
  }

  return utmParams;
}

/**
 * Send event to analytics API
 */
async function sendEvent(
  sessionId: string,
  eventType: string,
  pagePath: string,
  data: Record<string, unknown> = {},
): Promise<void> {
  try {
    const utmParams = captureUtmParams();
    await fetch(`${API_BASE}/api/v1/analytics/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event_type: eventType,
        session_id: sessionId,
        page_path: pagePath,
        referrer: document.referrer || undefined,
        ...utmParams,
        ...data,
      }),
    });
  } catch (error) {
    console.debug("Analytics event failed:", error);
  }
}

/**
 * Fetch Google Analytics settings from API with caching
 */
interface GASettings {
  google_analytics_id: string;
  custom_head_scripts: string;
  timestamp: number;
}

async function fetchGASettings(): Promise<GASettings | null> {
  // Check cache first
  const cached = sessionStorage.getItem(GA_SETTINGS_KEY);
  if (cached) {
    const parsed = JSON.parse(cached) as GASettings;
    if (Date.now() - parsed.timestamp < GA_SETTINGS_TTL) {
      return parsed;
    }
  }

  try {
    const response = await fetch(
      `${API_BASE}/api/v1/${ADMIN_ROUTE}/settings/public/analytics`,
    );
    if (response.ok) {
      const data = await response.json();
      const settings: GASettings = {
        google_analytics_id: data.google_analytics_id || "",
        custom_head_scripts: data.custom_head_scripts || "",
        timestamp: Date.now(),
      };
      sessionStorage.setItem(GA_SETTINGS_KEY, JSON.stringify(settings));
      return settings;
    }
  } catch (error) {
    console.debug("Failed to fetch GA settings:", error);
  }
  return null;
}

/**
 * Inject Google Analytics script
 */
function injectGoogleAnalytics(measurementId: string): void {
  if (!measurementId || measurementId.trim() === "") return;

  // Check if already injected
  if (
    document.querySelector(
      `script[src*="googletagmanager.com/gtag/js?id=${measurementId}"]`,
    )
  ) {
    return;
  }

  // Inject gtag.js script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Inject gtag config
  const configScript = document.createElement("script");
  configScript.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}');
  `;
  document.head.appendChild(configScript);

  console.debug("Google Analytics initialized:", measurementId);
}

/**
 * Inject custom head scripts (admin-controlled content only)
 * Note: This handles trusted admin input for analytics/tracking scripts
 */
function injectCustomHeadScripts(scripts: string): void {
  if (!scripts || scripts.trim() === "") return;

  // Check if already injected (use a marker)
  if (document.querySelector('meta[name="bb-custom-scripts-injected"]')) {
    return;
  }

  // Add marker
  const marker = document.createElement("meta");
  marker.name = "bb-custom-scripts-injected";
  marker.content = "true";
  document.head.appendChild(marker);

  // Parse script tags from the admin-provided content using regex
  // This is admin-controlled content from the settings API
  const scriptRegex = /<script([^>]*)>([\s\S]*?)<\/script>/gi;
  let match = scriptRegex.exec(scripts);

  while (match !== null) {
    const attributes = match[1];
    const content = match[2];

    const newScript = document.createElement("script");

    // Parse and set attributes
    const srcMatch = attributes.match(/src=["']([^"']+)["']/);
    if (srcMatch) {
      newScript.src = srcMatch[1];
    }

    const asyncMatch = attributes.match(/async/);
    if (asyncMatch) {
      newScript.async = true;
    }

    const deferMatch = attributes.match(/defer/);
    if (deferMatch) {
      newScript.defer = true;
    }

    // Set inline content if present
    if (content && content.trim()) {
      newScript.textContent = content;
    }

    document.head.appendChild(newScript);
    match = scriptRegex.exec(scripts);
  }

  console.debug("Custom head scripts injected");
}

interface AnalyticsProviderProps {
  children: ReactNode;
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const location = useLocation();
  const sessionId = getOrCreateSessionId();
  const [gaInitialized, setGaInitialized] = useState(false);

  // Load and inject Google Analytics on mount
  useEffect(() => {
    async function initGA() {
      const settings = await fetchGASettings();
      if (settings) {
        if (settings.google_analytics_id) {
          injectGoogleAnalytics(settings.google_analytics_id);
        }
        if (settings.custom_head_scripts) {
          injectCustomHeadScripts(settings.custom_head_scripts);
        }
        setGaInitialized(true);
      }
    }
    initGA();
  }, []);

  // Track page views on route change (both custom and GA)
  useEffect(() => {
    sendEvent(sessionId, "page_view", location.pathname);

    // Also send to Google Analytics if initialized
    if (
      gaInitialized &&
      typeof window !== "undefined" &&
      (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag
    ) {
      (window as unknown as { gtag: (...args: unknown[]) => void }).gtag(
        "event",
        "page_view",
        {
          page_path: location.pathname,
        },
      );
    }
  }, [location.pathname, sessionId, gaInitialized]);

  // Create tracking functions
  const trackEvent = (
    eventType: string,
    metadata?: Record<string, unknown>,
  ) => {
    sendEvent(sessionId, eventType, location.pathname, { metadata });
  };

  const trackFeature = (
    featureName: string,
    metadata?: Record<string, unknown>,
  ) => {
    sendEvent(sessionId, "feature_use", location.pathname, {
      feature_name: featureName,
      metadata,
    });
  };

  const trackSearch = (query: string, resultsCount?: number) => {
    sendEvent(sessionId, "search", location.pathname, {
      metadata: { query, results_count: resultsCount },
    });
  };

  const trackExport = (
    exportType: string,
    details?: Record<string, unknown>,
  ) => {
    sendEvent(sessionId, "export", location.pathname, {
      feature_name: exportType,
      metadata: details,
    });
  };

  const value: AnalyticsContextValue = {
    sessionId,
    trackEvent,
    trackFeature,
    trackSearch,
    trackExport,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}

/**
 * Hook to access analytics context
 */
export function useAnalyticsContext(): AnalyticsContextValue {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error(
      "useAnalyticsContext must be used within an AnalyticsProvider",
    );
  }
  return context;
}

export default AnalyticsProvider;
